import React, { useState, useEffect, useCallback } from "react";
import { Select } from "antd";
import { list as getAgentList } from "../controllers/V1/salesAgentControllers";

/**
 * Reusable Agent Select Component with Search and Select All functionality
 * 
 * @param {Array|String} value - Selected agent ID(s) - Array for multiple, String for single
 * @param {Function} onChange - Callback function when selection changes
 * @param {String} placeholder - Placeholder text (default: "Select Agents")
 * @param {String} className - CSS class name
 * @param {Object} style - Inline styles
 * @param {Number} maxTagCount - Maximum number of tags to show (default: 3)
 * @param {Boolean} showSearch - Enable search functionality (default: true)
 * @param {String} mode - Select mode: "multiple", "tags", etc. (default: "tags")
 * @param {Array} agentTypes - Filter agents by types (default: ["BDM", "Manager", "Sales Supervisor", "Sales Person"])
 * @param {Object} condition - Additional conditions for agent filtering
 * @param {Boolean} multiple - Enable multiple selection (default: true). Set to false for single selection
 * @param {Boolean} disableOptions - Disable options without crm_device_id (default: true)
 * @param {Boolean} showSelectAll - Show "Select All" option (default: true, only works when multiple=true)
 */
const AgentSelectWithSearch = ({
  value,
  onChange,
  placeholder = "Select Agents",
  className = "form-control store_input_field",
  style = { width: '100%' },
  maxTagCount = 3,
  showSearch = true,
  mode = "tags",
  condition = {},
  disabled = false,
  disableOptions = true,
  multiple = true,
  showSelectAll = true,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch agent data
  const getAgentData = useCallback(async () => {
    try {
      setLoading(true);
      const requestOptions = {
        type: "",
        condition: {
          status: "A",
          ...condition,
        },
        sort: { first_name: 1, last_name: 1 },
        select: { 
          first_name: 1, 
          last_name: 1, 
          phone: 1, 
          email: 1, 
          bind_with: 1,
          device_id: 1,
        },
        limit: 1000,
        skip: 0,
      };
      
      const res = await getAgentList(requestOptions);
      
      if (res?.status === true) {
        const agentOptions = res?.result?.map((item) => ({
          label: `${item?.first_name?.toUpperCase()} ${item?.last_name?.toUpperCase()} (${item?.phone})`,
          value: item?._id,
          ...(disableOptions ? {disabled: item?.device_id ? false : true} : {}),
          first_name: item?.first_name,
          last_name: item?.last_name,
          device_id: item?.device_id,
          phone: item?.phone,
          email: item?.email,
          bind_with: item?.bind_with,
        }));
        // Add "Select All" option at the beginning (only for multiple mode)
        if (multiple && showSelectAll) {
          setOptions([
            {
              label: "SELECT ALL",
              value: "select_all",
              disabled: false,
              first_name: "SELECT ALL",
              last_name: "",
              phone: "",
              email: "",
              bind_with: "",
            },
            ...agentOptions,
          ]);
        } else {
          setOptions(agentOptions);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching agents: ", error);
      setLoading(false);
    }
  }, [condition, disableOptions, multiple, showSelectAll]);

  // Load agents on mount and when relevant props change
  useEffect(() => {
    getAgentData();
  }, []);

  // Handle selection change
  const handleChange = (selectedValues) => {
    try {
      // Single selection mode - just pass the value directly
      if (!multiple) {
        onChange && onChange(selectedValues);
        return;
      }

      // Multiple selection mode with Select All functionality
      const currentValue = value || [];
      const allAgentIds = options
        .filter((option) => option.value !== "select_all" && !option.disabled)
        .map((option) => option.value);

      // Check if 'select_all' is in the new value
      const hasSelectAll = selectedValues.includes("select_all");

      // Check if all agents were previously selected
      const wasAllSelected = currentValue.length === allAgentIds.length && allAgentIds.length > 0;

      // Remove 'select_all' from value to get actual agent selections
      const filteredValue = selectedValues.filter((v) => v !== "select_all");

      // Scenario 1: Agent clicked 'Select All' from dropdown
      if (hasSelectAll && !wasAllSelected) {
        onChange && onChange(allAgentIds);
      }
      // Scenario 2: Agent removed the 'Select All' tag
      else if (wasAllSelected && !hasSelectAll && filteredValue.length === 0) {
        onChange && onChange([]);
      }
      // Scenario 3: Normal operation - agent is adding/removing individual agents
      else {
        onChange && onChange(filteredValue);
      }
    } catch (error) {
      console.error("Error handling change: ", error);
    }
  };

  // Calculate display value
  const displayValue = (() => {
    // Single selection mode - return value as is
    if (!multiple) {
      return value || undefined;
    }

    // Multiple selection mode - handle Select All
    const currentValue = value || [];
    const allAgentIds = options
      .filter((option) => option.value !== "select_all" && !option.disabled)
      .map((option) => option.value);
    
    // Show "Select All" if all agents are selected
    if (showSelectAll && currentValue.length === allAgentIds.length && allAgentIds.length > 0) {
      return ["select_all", ...currentValue];
    }
    return currentValue;
  })();

  return (
    <Select
      className={className}
      mode={multiple ? mode : undefined}
      style={style}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      options={options}
      showSearch={showSearch}
      loading={loading}
      disabled={disabled}
      maxTagCount={multiple ? maxTagCount : undefined}
      maxTagPlaceholder={multiple ? (omittedValues) => `+${omittedValues.length}` : undefined}
      dropdownStyle={{ zIndex: 10000 }}
      getPopupContainer={(trigger) => trigger.parentNode}
      filterOption={(input, option) => {
        const searchValue = input.toLowerCase();
        return (
          option.first_name?.toLowerCase()?.includes(searchValue) ||
          option.last_name?.toLowerCase()?.includes(searchValue) ||
          option.phone?.toString()?.toLowerCase()?.includes(searchValue) ||
          option.email?.toLowerCase()?.includes(searchValue) ||
          option.bind_with?.users_name?.toLowerCase()?.includes(searchValue) ||
          option.bind_with?.last_name?.toLowerCase()?.includes(searchValue) ||
          option.label?.toLowerCase()?.includes(searchValue)
        );
      }}
      optionRender={(option) => {
        if (option.data.value === "select_all") {
          return (
            <div style={{ fontWeight: 600, color: "#1890ff", padding: "5px 0" }}>
              {option.data.first_name}
            </div>
          );
        }
        return (
          <div>
            <div style={{ fontWeight: 600 }}>
              {option.data.first_name?.toUpperCase()} {option.data.last_name?.toUpperCase()}
            </div>
            {option.data.phone && (
              <div style={{ fontSize: 12, color: "#666" }}>
                {option.data.phone}
              </div>
            )}
            {option.data.email && (
              <div style={{ fontSize: 12, color: "#666" }}>
                {option.data.email}
              </div>
            )}
            {option.data.bind_with && (
              <div style={{ fontSize: 12, color: "#888" }}>
                Bind: {option.data.bind_with?.users_name?.toUpperCase()} {option.data.bind_with?.last_name?.toUpperCase()}
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

export default AgentSelectWithSearch;


